import { createAsyncThunk } from '@reduxjs/toolkit'

import type { SegmentActivationPreference } from 'api/segments'
import type { SegmentSaveData } from 'slices/aiConversations/segmentVisualizationTypes'
import { saveSegmentThunk } from 'slices/aiConversations/thunks'
import {
  activationSaveFailed,
  activationSaveFinished,
  activationSaveStarted,
  flowCompleted,
  nameEdited,
  nameSubmitted,
  setSegmentSaving,
} from 'slices/aiSegmentBuilder'
import type { ActivationFlowState } from 'slices/aiSegmentBuilder/types'
import { errorHandler } from 'utilities/errorHandler'

type AppState = {
  aiSegmentBuilder: {
    activationFlow: ActivationFlowState
  }
}

/**
 * Decides whether to open the activation modal or save directly.
 * Called when the user submits a name in the name modal.
 * Implements: separation-of-concerns (item 7) — all branching lives here,
 * not in the UI callback.
 */
export const segmentNameSubmittedThunk = createAsyncThunk<
  void,
  {
    name: string
    autoApproveEnabled: boolean
    preference: SegmentActivationPreference | undefined
    buildSegmentData: (
      name: string,
      autoApprove?: boolean
    ) => Omit<SegmentSaveData, 'name'>
  }
>(
  'aiSegmentBuilder/segmentNameSubmitted',
  async (
    { name, autoApproveEnabled, preference, buildSegmentData },
    { dispatch, getState }
  ) => {
    const state = getState() as AppState
    const { pendingAutoApprove } = state.aiSegmentBuilder.activationFlow

    // Flag off — skip modal entirely
    if (!autoApproveEnabled) {
      dispatch(flowCompleted())
      dispatch(setSegmentSaving(true))
      dispatch(saveSegmentThunk({ name, ...buildSegmentData(name) }))
      return
    }

    // Preference already saved or session choice set — skip modal
    if (
      preference?.autoApproveAiSegments != null ||
      pendingAutoApprove != null
    ) {
      const autoApprove =
        pendingAutoApprove != null
          ? pendingAutoApprove === 'activate'
          : preference?.autoApproveAiSegments ?? false
      dispatch(flowCompleted())
      dispatch(setSegmentSaving(true))
      dispatch(
        saveSegmentThunk({ name, ...buildSegmentData(name, autoApprove) })
      )
      return
    }

    // No preference — open activation modal
    dispatch(nameEdited(name))
    dispatch(nameSubmitted())
  }
)

/**
 * Called when the user clicks Save inside the activation modal.
 * Handles PATCH, records session choice, calls saveSegmentThunk.
 * Implements: fix silent PATCH failure (item 8) + separation-of-concerns.
 */
export const activationSaveClickedThunk = createAsyncThunk<
  void,
  {
    updatePreference: (params: SegmentActivationPreference) => Promise<unknown>
    currentPreference: SegmentActivationPreference | undefined
    buildSegmentData: (
      name: string,
      autoApprove: boolean
    ) => Omit<SegmentSaveData, 'name'>
  }
>(
  'aiSegmentBuilder/activationSaveClicked',
  async (
    { updatePreference, currentPreference, buildSegmentData },
    { dispatch, getState }
  ) => {
    const state = getState() as AppState
    const { segmentName, pendingAutoApprove, rememberChoice, flowOrigin } =
      state.aiSegmentBuilder.activationFlow
    const autoApprove = pendingAutoApprove === 'activate'

    if (!segmentName) return

    dispatch(activationSaveStarted())

    try {
      if (rememberChoice && autoApprove) {
        await updatePreference({ autoApproveAiSegments: true })
      } else if (
        autoApprove &&
        !rememberChoice &&
        currentPreference?.autoApproveAiSegments === true
      ) {
        await updatePreference({ autoApproveAiSegments: null })
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save preference'
      errorHandler.capture(
        '[SegmentActivationModal] Failed to save preference',
        { error: err }
      )
      dispatch(activationSaveFailed(message))
      return
    }

    dispatch(activationSaveFinished())

    // change-preference flow ends here — activationSaveFinished sets phase='naming'
    // so the name modal reopens. The segment save fires later from the name modal submit.
    if (flowOrigin === 'initial-save') {
      dispatch(setSegmentSaving(true))
      dispatch(
        saveSegmentThunk({
          name: segmentName,
          ...buildSegmentData(segmentName, autoApprove),
        })
      )
    }
  }
)
